import parse from 'date-fns/toDate';
import isDate from 'date-fns/isDate';
import momentTZ from 'moment-timezone';

export { parse, isDate };

export const getCurrentDate = () => {
  let today = new Date();
  let dd = today.getDate();
  let mm = today.getMonth() + 1;
  const yyyy = today.getFullYear();

  if (dd < 10) {
    dd = `${dd}`;
  }

  if (mm < 10) {
    mm = `${mm}`;
  }

  today = `${mm}/${dd}/${yyyy}`;
  return today;
};

export const convertDateToMonthDayYearFormat = date => {
  const d = parse(date);
  const month = `${d.getMonth() + 1}`;
  const day = `${d.getDate()}`;
  const year = d.getFullYear();

  return [month, day, year].join('/');
};

export const getTimeString = (
  date,
  isTwelveHour,
  hourMinuteSeparator = ':',
  am = ' AM',
  pm = ' PM'
) => {
  let hour = date.getHours();
  const amPm = hour < 12 ? am : pm;
  if (isTwelveHour) {
    hour = hour <= 12 ? hour || 12 : hour - 12;
  }
  let minutes = date.getMinutes();
  minutes = minutes > 9 ? minutes : `0${minutes}`;
  return `${hour}${hourMinuteSeparator}${minutes}${isTwelveHour ? `${amPm}` : ''}`;
};

export const getIndexOfDayInWeek = (date, dayWeekBegins = 0) =>
  (date.getDay() - dayWeekBegins + 7) % 7;

export const getFirstDayOfWeek = (date, dayWeekBegins) => {
  const newDate = parse(date);
  const indexOfDay = getIndexOfDayInWeek(newDate, dayWeekBegins);

  return new Date(newDate.setDate(newDate.getDate() - indexOfDay));
};

export const getLastDayOfWeek = (date, dayWeekBegins) => {
  const newDate = parse(date);
  const indexOfDay = getIndexOfDayInWeek(newDate, dayWeekBegins);

  return new Date(newDate.setDate(newDate.getDate() - (indexOfDay - 6)));
};

export const getDateByWeeksOffset = (weeksOffset, date) => {
  const dateVerified = date || new Date();
  const newDate = parse(dateVerified);
  return new Date(newDate.setDate(newDate.getDate() + weeksOffset * 7));
};

export const getDateInNDays = (date, n) => {
  const newDate = parse(date);
  return new Date(newDate.setDate(newDate.getDate() + n));
};

export const getFirstDayOfMonth = date => new Date(date.getFullYear(), date.getMonth(), 1);

export const getLastDayOfMonth = date => new Date(date.getFullYear(), date.getMonth() + 1, 0);

export const isDateWithinRange = (selectedDate, startDate, endDate) =>
  selectedDate - startDate >= 0 && endDate - selectedDate >= 0;

export const getIcsFormattedDate = date =>
  `${`0000${date.getFullYear().toString()}`.slice(-4) +
    `00${(date.getMonth() + 1).toString()}`.slice(-2) +
    `00${date.getDate().toString()}`.slice(-2)}T${`00${date.getHours().toString()}`.slice(
    -2
  )}${`00${date.getMinutes().toString()}`.slice(-2)}${`00${date.getSeconds().toString()}`.slice(
    -2
  )}`;

export const monthEnum = {
  0: 'January',
  1: 'February',
  2: 'March',
  3: 'April',
  4: 'May',
  5: 'June',
  6: 'July',
  7: 'August',
  8: 'September',
  9: 'October',
  10: 'November',
  11: 'December',
};

export const dayOfWeekEnum = {
  0: 'Sunday',
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
};

/**
 * Constructs a Date object based on the provided time and timezone.
 *
 * @param {string} time - The time in "HH:mm" format.
 * @param {string} timezone - The timezone identifier (e.g., "Europe/Zurich").
 * @returns {Date} The constructed Date object.
 */

const constructDateTime = (time, timezone) => {
  const [hours, minutes] = time.split(':');
  const dateTime = momentTZ(new Date()).tz(timezone);

  dateTime.set({
    hour: Number(hours),
    minute: Number(minutes),
    second: 0,
    millisecond: 0,
  });

  return dateTime.toDate();
};

/**
 * Gets the localized time frame based on start time, end time, and timezone.
 *
 * @param {Object} timeFrame - The parameters for the function.
 * @param {string} timeFrame.startTime - The start time in "HH:mm" format.
 * @param {string} timeFrame.endTime - The end time in "HH:mm" format.
 * @param {string} timeFrame.timeZone - The timezone identifier (e.g., "Europe/Zurich").
 * @param {string} inputLocale - The locale string (e.g., "de_CH").
 * @returns {Object} An object containing the formatted start time, end time, and timezone abbreviation.
 */
export const getLocalizedTimeFrame = (timeFrame, inputLocale) => {
  const { startTime, endTime, timeZone } = timeFrame;
  const locale = inputLocale.toLowerCase().replace('_', '-');

  const startDateTime = constructDateTime(startTime, timeZone);
  const endDateTime = constructDateTime(endTime, timeZone);

  const timeFormatOptions = {
    hour: 'numeric',
    minute: 'numeric',
    timeZone,
  };

  const formattedStartTime = new Intl.DateTimeFormat(locale, timeFormatOptions).format(
    startDateTime
  );
  const formattedEndTime = new Intl.DateTimeFormat(locale, timeFormatOptions).format(endDateTime);

  const timeZoneAbbreviation = momentTZ.tz(timeZone).format('z');

  return {
    startTime: formattedStartTime,
    endTime: formattedEndTime,
    timeZone: timeZoneAbbreviation,
  };
};
